import { memo, useCallback } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/chillnn/cn";

import { Label } from "@/components/ui/label/label";
import { TableRow } from "@/components/ui/table/table";

import {
	RATE_MATRIX_HIGHLIGHT_ATTR,
	RATE_MATRIX_HIGHLIGHT_CROSS_CLASS,
	RATE_MATRIX_HIGHLIGHT_OVERLAY_CLASS,
} from "./highlight-class";
import { getPlanEditPath } from "../../../model/get-plan-edit-path";
import { isPseudoPlanID } from "../../../model/is-pseudo-plan-id";
import { isExceptionPriceSource, RATE_MATRIX_PRICE_SOURCE_LABEL } from "../../../model/types";

import type { PlanGroupedRateMatrixRow } from "../../../model/group-rows-by-plan-id";

type HoverTarget = {
	rowId: string;
	date: string;
};

type Props = {
	hotelID: string;
	row: PlanGroupedRateMatrixRow;
	dates: string[];
	today: string;
	isRowHighlighted: boolean;
	highlightedDate: string | null;
	onHoverCell: (target: HoverTarget | null) => void;
};

const nameLinkClassName = "underline-offset-4 hover:underline";

/**
 * プラン見出し専用行。料金セルは空（プラン×部屋の明細行と役割を分ける）。
 */
export const PlanRateMatrixPlanHeaderRow = memo(
	({ hotelID, row, dates, today, isRowHighlighted, highlightedDate, onHoverCell }: Props) => {
		const planHeaderRowId = `plan-header-${row.planID}`;
		const planEditPath = getPlanEditPath({
			hotelID,
			planID: row.planID,
			isDayTrip: row.isDayTrip,
		});
		const showPlanDayTripLabel = row.isDayTrip;
		const showPriceSourceLabel = !isPseudoPlanID(row.planID) && isExceptionPriceSource(row.priceSource);

		const handleHoverDate = useCallback(
			(date: string) => {
				onHoverCell({ rowId: planHeaderRowId, date });
			},
			[onHoverCell, planHeaderRowId],
		);

		return (
			<TableRow className="relative isolate z-0 border-t border-t-zinc-200 bg-white transition-none hover:bg-white">
				<td
					className={cn(
						"sticky left-0 z-10 w-64 min-w-64 border-r border-r-zinc-200 bg-white px-3 py-2 text-left align-top text-zinc-950 hover:bg-white",
						isRowHighlighted && RATE_MATRIX_HIGHLIGHT_OVERLAY_CLASS,
					)}
					{...(isRowHighlighted ? { [RATE_MATRIX_HIGHLIGHT_ATTR]: "row" } : {})}
				>
					<div className="flex flex-col gap-y-1">
						{showPlanDayTripLabel || row.isSCConnected ? (
							<div className="flex flex-wrap gap-1">
								{showPlanDayTripLabel ? <Label variant="green" text="日帰り" size="xs" /> : null}
								{row.isSCConnected ? <Label variant="blue" text="サイコン連携" size="xs" /> : null}
							</div>
						) : null}
						{planEditPath ? (
							<Link
								to={planEditPath}
								className={cn("text-sm font-medium leading-snug", nameLinkClassName)}
								viewTransition
							>
								{row.planName}
							</Link>
						) : (
							<p className="text-sm font-medium leading-snug">{row.planName}</p>
						)}
						{showPriceSourceLabel ? (
							<p className="text-2xs leading-snug text-caption-50">
								{RATE_MATRIX_PRICE_SOURCE_LABEL[row.priceSource]}
							</p>
						) : null}
					</div>
				</td>

				{dates.map((date) => {
					const isColumnHighlighted = highlightedDate === date;
					const isCrossHighlighted = isRowHighlighted && isColumnHighlighted;
					return (
						<td
							key={`${planHeaderRowId}-${date}`}
							aria-hidden
							className={cn(
								"min-h-14 w-20 min-w-20 border-r border-r-zinc-200 bg-zinc-50 p-1",
								date === today && "border-l border-x-zinc-400",
								isCrossHighlighted
									? RATE_MATRIX_HIGHLIGHT_CROSS_CLASS
									: (isRowHighlighted || isColumnHighlighted) && RATE_MATRIX_HIGHLIGHT_OVERLAY_CLASS,
							)}
							{...(isCrossHighlighted
								? { [RATE_MATRIX_HIGHLIGHT_ATTR]: "cross" }
								: isRowHighlighted || isColumnHighlighted
									? {
											[RATE_MATRIX_HIGHLIGHT_ATTR]: isRowHighlighted ? "row" : "column",
										}
									: {})}
							onMouseEnter={() => handleHoverDate(date)}
						/>
					);
				})}
			</TableRow>
		);
	},
);

PlanRateMatrixPlanHeaderRow.displayName = "PlanRateMatrixPlanHeaderRow";
